import {
    AgencyProfileOption,
    AttributeOption,
    CadSearchResource,
    CallForServiceOption,
    ColumnDefinitionEnum,
    DispatchAreaOption,
    EventLabelOption,
    StationOption,
    SubdivisionOption,
    UnitStateOption,
    UnitStatusSearchOptions,
} from '@mark43/cad-api';
import { UnitQueueStore } from '../store/unit-queue-store';
import { useQueueStore } from '../../queues/context/use-queue-store';
import { useMemo } from 'react';
import type { WithColumnDefinition } from '../../event-queues/get-event-filter-options';
import { useEventPanelOptions } from '../../event-queues/get-event-filter-options';
import { useApiClient } from '@m43/core';
import { useAttributesByType } from '../../attributes/use-attributes-by-type';
import {
    AttributeOptionWithType,
    useAttributeOptionsByType,
} from '../../attributes/use-attribute-options-by-type';
import { getAttributeOptionsForQueueType } from '../../queues/utils/get-attribute-options-for-queue-type';
import { globalAttributes } from '../../common/global-attributes';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import {
    getPrimarySubdivisionOptions,
    PrimarySubdivisionNames,
} from '../../map-window/components/helpers';
import { useBootstrapState } from '../../bootstrap/bootstrap-provider';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { Option } from '../../forms/option';
import { useIsAgencyTypeGlobalAttributeIdConsidered } from '../../common/hooks/use-is-agency-type-global-attribute-id-considered';

export const AVAILABLE = 'Available' as const;
export const UNAVAILABLE = 'Unavailable' as const;

export interface AvailabilityOption extends Option {
    label: typeof AVAILABLE | typeof UNAVAILABLE;
    value: 0 | 1;
}

const AVAILABILITY_OPTIONS: AvailabilityOption[] = [
    {
        label: AVAILABLE,
        value: 1,
    },
    {
        label: UNAVAILABLE,
        value: 0,
    },
];

export interface UnitQueueFilterOptions {
    additionalCfsOptions: WithColumnDefinition<CallForServiceOption>[];
    cfsOptions: WithColumnDefinition<CallForServiceOption>[];
    priorityOptions: WithColumnDefinition<AttributeOption>[];
    alarmLevelOptions: WithColumnDefinition<AttributeOption>[];
    eventLabelOptions: WithColumnDefinition<EventLabelOption>[];
    dispatchAreaOptions: WithColumnDefinition<DispatchAreaOption>[];
    unitDispatchAreaOptions: WithColumnDefinition<DispatchAreaOption>[];
    agencyOptions: WithColumnDefinition<AgencyProfileOption>[];
    stationOptions: WithColumnDefinition<StationOption>[];
    unitTypeOptions: WithColumnDefinition<AttributeOption>[];
    unitStateOptions: WithColumnDefinition<UnitStateOption>[];
    equipmentTypeOptions: WithColumnDefinition<AttributeOptionWithType>[];
    patrolTypeOptions: WithColumnDefinition<AttributeOptionWithType>[];
    skillsTypeOptions: WithColumnDefinition<AttributeOptionWithType>[];
    fireUnitTypeOptions: WithColumnDefinition<AttributeOption>[];
    policeUnitTypeOptions: WithColumnDefinition<AttributeOption>[];
    emsUnitTypeOptions: WithColumnDefinition<AttributeOption>[];
    unitPrimarySubdivisionOptions: WithColumnDefinition<SubdivisionOption>[];
    eventPrimarySubdivisionOptions: WithColumnDefinition<SubdivisionOption>[];
    availabilityOptions: WithColumnDefinition<AvailabilityOption>[];
}

const emptyOptions: UnitStatusSearchOptions = {
    agencyProfileOptions: [],
    agencyTypeOptions: [],
    assignmentTypeOptions: [],
    callForServiceOptions: [],
    callSignOptions: [],
    countryCodeOptions: [],
    dispatchAreaOptions: [],
    eventOriginOptions: [],
    groupedSubdivisionOptions: [],
    hospitalOptions: [],
    locationVerificationOptions: [],
    priorityOptions: [],
    stateOptions: [],
    stationOptions: [],
    timeRangeOptions: [],
    unitStateOptions: [],
    vehicleTagOptions: [],
    eyeColorOptions: [],
    hairColorOptions: [],
    itemColorOptions: [],
    personInvolvementTypeOptions: [],
    raceOptions: [],
    sexOptions: [],
    towTypeOptions: [],
    vehicleInvolvementTypeOptions: [],
};

export type EnsuredQueryResult<TData> = Omit<UseQueryResult<TData>, 'data'> & { data: TData };

export function useStationAndStatusOptions(): EnsuredQueryResult<UnitStatusSearchOptions> {
    const apiClient = useApiClient();

    const query = useQuery(
        ['unit-status-search-bootstrap'],
        async () => {
            const resp = await apiClient.request<CadSearchResource.GetUnitStatusSearchBootstrap>({
                method: 'POST',
                url: 'cad_search/unit_status/bootstrap',
                data: {},
            });
            return resp.data.data;
        },
        { refetchOnMount: false, cacheTime: Infinity, staleTime: Infinity }
    );

    return useMemo(() => {
        return {
            ...query,
            data: query.data ? query.data.unitStatusSearchOptions : emptyOptions,
        } as EnsuredQueryResult<UnitStatusSearchOptions>;
    }, [query]);
}

export function useUnitQueueFilterOptions(): UnitQueueFilterOptions {
    const { eventqueuefilteroptions: filterOptions } = useEventPanelOptions();

    const {
        data: { stationOptions, unitStateOptions },
    } = useStationAndStatusOptions();
    const {
        subdivisionOptionsBootstrapResponse,
        cadDepartmentAgencyView,
        primaryEmsSubdivisionName,
        primaryPoliceSubdivisionName,
        primaryFireSubdivisionName,
    } = useBootstrapState();

    const { multiAgencySubdivisions } = useEnabledFeatures();

    const primarySubdivisionNames: PrimarySubdivisionNames = {
        primaryPoliceSubdivisionName,
        primaryFireSubdivisionName,
        primaryEmsSubdivisionName,
    };

    const { agencyId } = cadDepartmentAgencyView;

    const bootStrapView = multiAgencySubdivisions
        ? subdivisionOptionsBootstrapResponse?.agencyIdToSubdivisionOptionsBootstrapView?.[agencyId]
        : subdivisionOptionsBootstrapResponse?.combinedSubdivisionOptionsBootstrapView;

    const fireSubdivisionOptions = getPrimarySubdivisionOptions({
        subdivisionOptionsBootstrapView: bootStrapView,
        agencyTypeGlobalAttrId: globalAttributes.agencyTypeGlobal.FIRE,
        primarySubdivisionNames,
    });

    const policeSubdivisionOptions = getPrimarySubdivisionOptions({
        subdivisionOptionsBootstrapView: bootStrapView,
        agencyTypeGlobalAttrId: globalAttributes.agencyTypeGlobal.POLICE,
        primarySubdivisionNames,
    });

    const emsSubdivisionOptions = getPrimarySubdivisionOptions({
        subdivisionOptionsBootstrapView: bootStrapView,
        agencyTypeGlobalAttrId: globalAttributes.agencyTypeGlobal.EMS,
        primarySubdivisionNames,
    });

    const queueStore = useQueueStore() as UnitQueueStore;
    const { queueClasses } = queueStore.workspacePanel.queue;

    const isQueueType = useIsAgencyTypeGlobalAttributeIdConsidered();

    const {
        alarmLevelOptions,
        callForServiceOptions: cfsOptions,
        eventLabelOptions,
        emsAgencyProfileOptions,
        fireAgencyProfileOptions,
        policeAgencyProfileOptions,
        emsDispatchAreaOptions,
        fireDispatchAreaOptions,
        policeDispatchAreaOptions,
        priorityOptions,
    } = useMemo(() => filterOptions, [filterOptions]);

    const additionalCfsOptions = useMemo(() => cfsOptions, [cfsOptions]);

    // only return relevant dispatch area options depending on queue class
    const dispatchAreaOptions = useMemo(
        () => [
            ...(isQueueType(globalAttributes.agencyTypeGlobal.FIRE) ? fireDispatchAreaOptions : []),
            ...(isQueueType(globalAttributes.agencyTypeGlobal.POLICE)
                ? policeDispatchAreaOptions
                : []),
            ...(isQueueType(globalAttributes.agencyTypeGlobal.EMS) ? emsDispatchAreaOptions : []),
        ],
        [fireDispatchAreaOptions, policeDispatchAreaOptions, emsDispatchAreaOptions, isQueueType]
    );

    // only return relevant agency options depending on queue class
    const agencyOptions = useMemo(
        () => [
            ...(isQueueType(globalAttributes.agencyTypeGlobal.FIRE)
                ? fireAgencyProfileOptions
                : []),
            ...(isQueueType(globalAttributes.agencyTypeGlobal.POLICE)
                ? policeAgencyProfileOptions
                : []),
            ...(isQueueType(globalAttributes.agencyTypeGlobal.EMS) ? emsAgencyProfileOptions : []),
        ],
        [fireAgencyProfileOptions, policeAgencyProfileOptions, emsAgencyProfileOptions, isQueueType]
    );

    // only return relevant subdivision options depending on queue class
    const subdivisionOptions = useMemo(
        () => [
            ...(isQueueType(globalAttributes.agencyTypeGlobal.FIRE) ? fireSubdivisionOptions : []),
            ...(isQueueType(globalAttributes.agencyTypeGlobal.POLICE)
                ? policeSubdivisionOptions
                : []),
            ...(isQueueType(globalAttributes.agencyTypeGlobal.EMS) ? emsSubdivisionOptions : []),
        ],
        [fireSubdivisionOptions, policeSubdivisionOptions, emsSubdivisionOptions, isQueueType]
    );

    const eventPrimarySubdivisionOptions = useMemo(() => subdivisionOptions, [subdivisionOptions]);

    const { attributes: unitTypeAttributes } = useAttributesByType({
        attributeType: 'UNIT_TYPE',
        params: {
            include_is_none: true,
        },
    });

    const { attributeOptions: equipmentTypeOptions } = useAttributeOptionsByType({
        attributeType: 'EQUIPMENT_TYPE',
        params: {
            include_is_none: true,
        },
    });

    const { attributeOptions: skillsTypeOptions } = useAttributeOptionsByType({
        attributeType: 'USER_SKILL',
        params: {
            include_is_none: true,
        },
    });

    const { attributeOptions: virtualpatrolTypeOptions } = useAttributeOptionsByType({
        attributeType: 'VIRTUAL_PATROL',
        includeDisplayAbbreviation: true,
        params: {
            include_is_none: true,
        },
    });

    const { attributeOptions: directedpatrolTypeOptions } = useAttributeOptionsByType({
        attributeType: 'DIRECTED_PATROL',
        includeDisplayAbbreviation: true,
        params: {
            include_is_none: true,
        },
    });

    const patrolTypeOptions = useMemo(() => {
        return [...virtualpatrolTypeOptions, ...directedpatrolTypeOptions];
    }, [virtualpatrolTypeOptions, directedpatrolTypeOptions]);

    const unitTypeOptions = useMemo(
        () => getAttributeOptionsForQueueType(queueClasses, unitTypeAttributes),
        [queueClasses, unitTypeAttributes]
    );

    const fireUnitTypeOptions = useMemo(
        () =>
            unitTypeOptions.filter(
                (attr) => attr.parentAttributeId === globalAttributes.agencyTypeGlobal.FIRE
            ),
        [unitTypeOptions]
    );

    const emsUnitTypeOptions = useMemo(
        () =>
            unitTypeOptions.filter(
                (attr) => attr.parentAttributeId === globalAttributes.agencyTypeGlobal.EMS
            ),
        [unitTypeOptions]
    );

    const policeUnitTypeOptions = useMemo(
        () =>
            unitTypeOptions.filter(
                (attr) => attr.parentAttributeId === globalAttributes.agencyTypeGlobal.POLICE
            ),
        [unitTypeOptions]
    );

    return {
        additionalCfsOptions: additionalCfsOptions.map((option) => ({
            ...option,
            columnDefinition: ColumnDefinitionEnum.ADDITIONAL_CFS.name,
        })),
        cfsOptions: cfsOptions.map((option) => ({
            ...option,
            columnDefinition: ColumnDefinitionEnum.CFS_NUMBER_AND_TEXT.name,
        })),
        priorityOptions: priorityOptions.map((option) => ({
            ...option,
            columnDefinition: ColumnDefinitionEnum.PRIORITY.name,
        })),
        alarmLevelOptions: alarmLevelOptions.map((option) => ({
            ...option,
            columnDefinition: ColumnDefinitionEnum.ALARM_LEVEL.name,
        })),
        eventLabelOptions: eventLabelOptions.map((option) => ({
            ...option,
            columnDefinition: ColumnDefinitionEnum.EVENT_LABELS.name,
        })),
        dispatchAreaOptions: dispatchAreaOptions.map((option) => ({
            ...option,
            columnDefinition: ColumnDefinitionEnum.DISPATCH_AREA.name,
        })),
        unitDispatchAreaOptions: dispatchAreaOptions.map((option) => ({
            ...option,
            columnDefinition: ColumnDefinitionEnum.UNIT_DISPATCH_AREA.name,
        })),
        agencyOptions: agencyOptions.map((option) => ({
            ...option,
            columnDefinition: ColumnDefinitionEnum.UNIT_AGENCY.name,
        })),
        stationOptions: stationOptions.map((option) => ({
            ...option,
            columnDefinition: ColumnDefinitionEnum.STATION.name,
        })),
        unitTypeOptions: unitTypeOptions.map((option) => ({
            ...option,
            columnDefinition: ColumnDefinitionEnum.UNIT_TYPE.name,
        })),
        unitStateOptions: unitStateOptions.map((option) => ({
            ...option,
            columnDefinition: ColumnDefinitionEnum.STATUS.name,
        })),
        patrolTypeOptions: patrolTypeOptions.map((option) => ({
            ...option,
            columnDefinition: ColumnDefinitionEnum.PATROL_TYPE.name,
        })),
        fireUnitTypeOptions: fireUnitTypeOptions.map((option) => ({
            ...option,
            columnDefinition: ColumnDefinitionEnum.UNIT_TYPE.name,
        })),
        emsUnitTypeOptions: emsUnitTypeOptions.map((option) => ({
            ...option,
            columnDefinition: ColumnDefinitionEnum.UNIT_TYPE.name,
        })),
        policeUnitTypeOptions: policeUnitTypeOptions.map((option) => ({
            ...option,
            columnDefinition: ColumnDefinitionEnum.UNIT_TYPE.name,
        })),
        equipmentTypeOptions: equipmentTypeOptions.map((option) => ({
            ...option,
            columnDefinition: ColumnDefinitionEnum.EQUIPMENT.name,
        })),
        skillsTypeOptions: skillsTypeOptions.map((option) => ({
            ...option,
            columnDefinition: ColumnDefinitionEnum.SKILLS.name,
        })),
        unitPrimarySubdivisionOptions: subdivisionOptions.map((option) => ({
            ...option,
            columnDefinition: ColumnDefinitionEnum.UNIT_BEAT.name,
        })),
        eventPrimarySubdivisionOptions: eventPrimarySubdivisionOptions.map((option) => ({
            ...option,
            columnDefinition: ColumnDefinitionEnum.BEAT.name,
        })),
        availabilityOptions: AVAILABILITY_OPTIONS.map((option) => ({
            ...option,
            columnDefinition: ColumnDefinitionEnum.AVAILABILITY.name,
        })),
    };
}
